import { BlazeDropdownItem } from 'components/ui/dropdown/BlazeDropdown/BlazeInputDropdown';
import { DataSettings } from 'types/rest/blazeDataTypes';
import { MessagingMappings, MessagingSourceType, MessagingTransactionMapping } from './assetCatalogTypes';

export type MockService = MockServiceTxn & MockServiceMar & MockServiceMessaging;

// type TRANSACTIONAL
export interface MockServiceTxn extends MockServiceBase {
  endpointPreference: string;
  liveSystemHost: string;
  liveSystemPort: number;
  noMatchingRequestPreference: string;
  mockServiceTransactions: MockServiceTransaction[];
  mockServiceRecordings?: [];
  showHostPortFlag: boolean;
  responseDelay?: ResponseDelay;
  assets?: AssetAssignment[];
  configKeyAssets?: AssetAssignment[];
  containerName: string;
  httpRunnerEnabled: boolean;
  messagingRunnerEnabled: boolean;
  isRecorderModeActive?: boolean;
  isHttpRecorderModeActive?: boolean;
  recorderStatus: RecorderStatus;
  httpRecorderStatus: RecorderStatus;
  httpRunnerOverrideKeystore?: boolean;
  httpRunnerOverrideTruststore?: boolean;
}

// type MESSAGING
export interface MockServiceMessaging extends MockServiceBase {
  brokerConfig: BrokerConfigDto;
  classPathJars: ClassPathJarsDto;
  messagingProtocol: MessagingProtocol;
}

export interface MockServiceEndpoint {
  endpoint: string;
  runnerType: RunnerType;
  runnerSubType: string;
}

export enum RunnerType {
  HTTP = 'HTTP',
  MESSAGING = 'MESSAGING',
}

export enum RecorderStatus {
  INACTIVE = 'INACTIVE',
  RUNNING = 'RUNNING',
  STOPPED = 'STOPPED',
  FAILED = 'FAILED',
}

export enum MessagingProtocol {
  IBM_MQ9_JMS = 'IBM_MQ9_JMS',
  IBM_MQ9_NATIVE = 'IBM_MQ9_NATIVE',
  ACTIVE_MQ_CLASSIC = 'ACTIVE_MQ_CLASSIC',
  ACTIVE_MQ_ARTEMIS = 'ACTIVE_MQ_ARTEMIS',
  KAFKA = 'KAFKA',
}

export enum BrokerType {
  EMBEDDED = 'EMBEDDED',
  EXTERNAL = 'EXTERNAL',
}

export interface BrokerConfigDto {
  hostname: string;
  port: number | string | null;
  channel: string;
  queueManager: string;
  username?: string;
  password?: string;
  queues: QueueConfigDto[];
  topics: TopicConfigDto[];
  subscriptions: SubscriptionsDto[];
  embeddedBroker: boolean;
  flowConfigurations?: FlowConfiguration[];
  sslAuthentication?: boolean;
  sslCipherSuite?: string;
  autoOffsetReset?: string;
  numPartitions?: number;
}

export interface FlowConfiguration {
  name: string;
  transactionMapping?: MessagingMappings;
}

export interface SubscriptionsDto {
  name: string;
}

export interface QueueConfigDto {
  name: string;
}

export interface TopicConfigDto {
  name: string;
  isDurable: boolean;
  durableSubscriptionName: string;
}

export interface ClassPathJarsDto {
  paths: string[];
}

export interface AssetAssignment {
  assetUsageType: AssetUsageType;
  assetId?: number;
  alias?: string;
  configurationKey?: string;
}

export type AssetUsageType =
  | 'CLIENT_KEYSTORE_CERT' // Two Way SSL
  | 'CLIENT_KEYSTORE' //Redirect to Live
  | 'CLIENT_TRUSTSTORE_CERT' //Redirect to Live
  | 'SERVER_KEYSTORE' //One Way SSL (server)
  | 'SERVER_KEYSTORE_CERT' //One Way SSL (server)
  | 'SERVER_KEYSTORE_TRUSTSTORE' //Two Way SSL (server)
  | 'CLIENT_KEYSTORE_TRUSTSTORE'; //Two Way SSL (client)

export interface AnalyticsMetaDataDto {
  serviceMockId: number;
  name: string;
  svEndpoints: any[];
  locationName: string;
  harborId: string;
  shipId: string;
  serviceName: string;
  serviceId: string;
  type: string;
  status: string;
  metadata: any;
  httpEndpoint: string;
  httpsEndpoint: string;
  endpoints: MockServiceEndpoint[];
}

export type MsType = typeof SV | typeof TRANSACTIONAL | null;

export interface MockServiceAnalytics {
  id: number;
  name: string;
  type: MsType;
  deleted?: boolean;
  meta?: AnalyticsMetaDataDto;
}

export interface ResponseDelay {
  type: DelayType;
  sigma: number;
  upper: number;
  lower: number;
  median: number;
  duration: number;
  dribbled?: boolean;
  chunks?: number;
}

// type SV
export interface MockServiceMar extends MockServiceBase {
  capacity: number;
  groupTag: string;
  assetId: number | null;
  bridgeId: number | null;
  bridgeName: string;
  vseId: number | null;
  vseName: string;
  registryId: number | null;
  registryName: string;
  svMessage?: string;
  svEndpoint?: string[];
  executionMode?: string;
}

export type MockServiceStatus = 'RUNNING' | 'CONFIGURING' | 'STOPPED' | 'FAILED';

export type MockServiceUpgradeOptions = 'MOCK' | 'ALLMOCKS';

export type MockServiceType = typeof SV | typeof TRANSACTIONAL | typeof MESSAGING | null;

interface MockServiceBase {
  id: number;
  thinkTime: number;
  name: string;
  harborId: string | null;
  shipId: string | null;
  description: string;
  status: MockServiceStatus | string;
  httpEndpoint: string;
  httpsEndpoint: string;
  serviceId: number;
  serviceName: string;
  templateId: number;
  created: string;
  createdDate: string;
  createdBy: string;
  updated: string;
  updatedBy: string;
  replicas: number;
  metaData: MockServiceMetaData;
  original: MockServiceOriginalValues;
  type: MockServiceType;
  deploymentDateTime: number;
  hits: number;
  configuredAt: number;
  endpoints: MockServiceEndpoint[];
  /**
   * Indicates whether SM ever ran and sent at least one heartbeat.
   */
  lastHeartBeat: string;
  /**
   * Last time the mock handled a request, populated by the scheduled analytics poller.
   */
  lastUsedAt?: string;
  /**
   * Indicates a draft object.
   */
  isDraft?: boolean;

  /**
   * Points to the original serviceMock ID when we work with the draft.
   */
  serviceMockId?: number;

  /**
   * Indicates it is a brand new virtual service that was never run.
   */
  isNew?: boolean;

  /**
   * Iteration settings for blaze data.
   */
  dataSettings?: DataSettings;

  /**
   * Link to shared folder with a full blaze data file
   */
  blazeDataFile?: string;

  /**
   * Link to shared folder with a blaze data preview file
   */
  blazeDataPreviewFile?: string;

  /**
   * Link to shared folder to model used for data generation during the last deploy / reconfigure.
   */
  modelFile?: any;

  /**
   * ID of the template which was used during the creation of the virtual service.
   */
  appliedTemplateId?: number;

  /**
   * Indicates if the template is still active. When there is a change between template and virtual service, the flag
   * is set to false.
   */
  isTemplateActive?: boolean;
  metadata?: MetaData;
  preferredPortWarningMsg?: string;
  /**
   * ID of last tracking action of this virtual service.
   */
  lastActionTrackingId?: string;

  /**
   * Extended logging for OPL
   */
  loggingEnabled?: boolean;
  configurationId: number | null;
  secretsVaultId: number | null;
  proxy?: ProxyConfiguration;
  priorityMode?: PriorityMode;
  recorderConfigDto?: RecorderConfig;
  httpRecorderConfigDto?: HttpRecorderConfig;
  runtimeConfig: RuntimeConfig;
  loggingConfig?: ServiceMockLoggingConfig;
  /***
   * cachetype indicates to initialize SQL or NoSQL.
   */
  cacheType?: CacheType;
  initialSqlScript?: string;

  /**
   * Authentication settings for the HTTP runner (BASIC / HEADER / NONE).
   * When set, every WireMock stub requires the configured credentials.
   * Null indicates that auth settings have been explicitly cleared.
   */
  authSettings?: AuthSettings | null;

  /**
   * JVM heap (Xmx/Xms) override, OPL locations only. When disabled, the mock inherits the
   * harbor's own Xmx/Xms. Values are capped to the account's configured maximum at deploy time
   * if they exceed it — see MemorySettings.tsx.
   */
  jvmMemoryOverrideEnabled?: boolean;
  jvmOverrideXmxMb?: number | null;
  jvmOverrideXmsMb?: number | null;
}

export interface ServiceMockLoggingConfig {
  // Common
  containerFileLoggingEnabled: boolean;
  eventsFileLoggingEnabled: boolean;
  sslLoggingEnabled: boolean;
  // File Logging feature
  requestFileName: string;
  responseFileName: string;
  overwriteExistingFiles: boolean;
  advancedFileLoggingEnabled: boolean;
}

export type RuntimeConfig = {
  showLogsEnabled: boolean;
  showLogsMaxSize: number;
  inspectionDataEnabled: boolean;
  inspectionDataMaxRequestSize: number;
  inspectionDataMaxRequestsPerSecond: number;
  inspectionDataMaxActionsPerSecond: number;
  inspectionDataMaxBodySize: number;
  matcherLogsCacheMaxSize: number;
  consoleMatcherLogsEnabled: boolean;
  showLogsMatcherLogsEnabled: boolean;
  jettyContainerThreads: number | null;
  requestTimeout: number;
  scriptsEnabled: boolean;
  nodejsEnabled: boolean;
};

export type RecorderConfig = {
  maxMessagesCount: number;
  maxMessagesPerSecondCount: number;
  mappings: RecordingMapping[];
};

export type HttpRecorderConfig = {
  maxRequestSize: number;
  maxRequestsPerSecond: number;
  liveSystemUrl: string;
  assets?: AssetAssignment[];
  configKeyAssets?: AssetAssignment[];
};

export type RecordingMapping = {
  inboundDestination: string;
  outboundDestination: string;
  originType: MessagingSourceType;
};

export enum RuntimeKeyTypes {
  showLogsEnabled = 'showLogsEnabled',
  showLogsMaxSize = 'showLogsMaxSize',
  cacheStaticResponses = 'cacheStaticResponses',
  secretsEnabled = 'secretsEnabled',
  inspectionDataEnabled = 'inspectionDataEnabled',
  inspectionDataMaxRequestSize = 'inspectionDataMaxRequestSize',
  inspectionDataMaxRequestsPerSecond = 'inspectionDataMaxRequestsPerSecond',
  inspectionDataMaxActionsPerSecond = 'inspectionDataMaxActionsPerSecond',
  inspectionDataMaxBodySize = 'inspectionDataMaxBodySize',
  matcherLogsCacheMaxSize = 'matcherLogsCacheMaxSize',
  consoleMatcherLogsEnabled = 'consoleMatcherLogsEnabled',
  showLogsMatcherLogsEnabled = 'showLogsMatcherLogsEnabled',
  jettyContainerThreads = 'jettyContainerThreads',
  requestTimeout = 'requestTimeout',
  scriptsEnabled = 'scriptsEnabled',
  nodejsEnabled = 'nodejsEnabled',
}

export type ProxyConfiguration = {
  proxyUrl: string;
  username?: string;
  password?: string;
  assets?: AssetAssignment[];
  configKeyAssets?: AssetAssignment[];
  nonProxyHosts?: string;
};

export interface MetaData {
  preferredPort?: string;
  preferredMessagingRunnerPort?: string;
  version?: string;
  warnings?: Warnings;
  badges?: BadgesStatus;
  limits?: {
    percentage?: number;
  };
  stages?: { [key: string]: string };
}

export interface Warnings {
  locationUnknown?: boolean;
  upgradeAvailable?: boolean;
  analyticsDataMissing?: boolean;
  isLimitExceeded?: boolean;
}

export const SV = 'SV';
export const TRANSACTIONAL = 'TRANSACTIONAL';
export const MESSAGING = 'MESSAGING';

export interface MockServiceOriginalValues {
  id: number;
  name: string;
  description: string;
  serviceId: number;
  harborId: string | null;
  shipId: string | null;
  endpointPreference: string;
  thinkTime: number;
  liveSystemPort: number;
  liveSystemHost: string;
  replicas: number;
  noMatchingRequestPreference: string;
  mockServiceTransactions: MockServiceTransaction[];
  groupTag: string;
  capacity: number;
  assetId: number | null;
  serviceMockId: number | undefined;
  bridgeId: number | null;
  vseId: number | null;
  registryId: number | null;
}

export type MockServiceCapabilities = {
  isBlazeData: boolean;
  appliedTemplate: boolean;
};

/**
 * Actions which switch virtual service object to "in progress" state.
 */
export type MockServiceAction = 'MERGE' | 'RECONFIGURE' | 'DEPLOY' | 'STOP';

export interface MockServiceMetaData {
  isDeployed: boolean;
  inProgress: boolean;
  isFailed: boolean;
  isNewest: boolean;
  isDirty: boolean;
  isFileAssetDirty: boolean;
  isInvalid: boolean;
  isStopped: boolean;
  /** Indicates when data has changed. */
  dataChangedTs?: number;
  /** Virtual Service actions which trigger in progress boolean */
  inProgressAction?: MockServiceAction;
  trackingStatus?: string;
}

export interface BadgesStatus {
  WEBHOOK?: boolean;
  STATE_UPDATE?: boolean;
  HTTP_CALL?: boolean;
  DELAYED?: boolean;
  CONTAINS_EXPRESSIONS?: boolean;
}

export interface ServiceLocation {
  id: number;
  name: string;
}

export interface Service {
  id: number;
  name: string;
}

export interface ServiceLogState {
  apiVersion: number;
  error: [];
  requestId: string;
  result: ServiceLogs;
  isLoading: boolean;
  serviceMockId: number;
  uniqueWindowId: number;
  openWindowId: number;
}

export interface ServiceLogs {
  httpRunnerLog: ServiceLogItem[];
  messagingRunnerLog: ServiceLogItem[];
}

export interface ServiceLogItem {
  txnId: number;
  liveResponse: boolean;
  matchFound: boolean;
  matchType: string;
  matchedTxnId: string;
  requestTimeStamp: string;
  priority: number;
  request: ServiceLogResultRequest;
  response: ServiceLogResultResponse;
  matchingLog: MatchingLog;
}

export interface MatchingLog {
  log: MatchingTxLog[];
  st: number;
  fi: number;
}

export interface MatchingTxLog {
  id: number;
  name: string;
  log: [{ t: number; m: string }]; //TODO - check if this type definition is correct
  st: number;
  fi: number;
}

export interface ServiceLogResultRequest {
  url: string;
  method: string;
  body: string;
  cookies: object;
  queryParams: object;
  headers: object;
}

export interface ServiceLogResultResponse {
  status: number;
  body: string;
  headers: object;
}

export interface MockServiceTransaction {
  txnId: number;
  priority: number;
}

export enum DelayType {
  NONE = 'none',
  FIXED = 'fixed',
  LOGNORMAL = 'lognormal',
  UNIFORM = 'uniform',
}
export enum ExecutionMode {
  MOST_EFFICIENT = 'MOST_EFFICIENT',
  STAND_IN = 'STAND_IN',
  LIVE_INVOCATION = 'LIVE_INVOCATION',
  FAILOVER = 'FAILOVER',
  DYNAMIC = 'DYNAMIC',
}

export const execModesItems: BlazeDropdownItem[] = [
  { id: ExecutionMode.MOST_EFFICIENT, name: 'Most Efficient' },
  { id: ExecutionMode.STAND_IN, name: 'Stand In' },
  { id: ExecutionMode.LIVE_INVOCATION, name: 'Live Invocation' },
  { id: ExecutionMode.FAILOVER, name: 'Failover' },
  { id: ExecutionMode.DYNAMIC, name: 'Dynamic' },
];

export type UpdateConfig = {
  keepBlazeData: boolean;
  updateAppliedTemplate: boolean;
};

export enum PriorityMode {
  'default' = 'DEFAULT',
  'unique_priority' = 'UNIQUE_PRIORITY',
}

export type CacheType = 'NO_SQL' | 'SQL';

export interface SecretsVault {
  id: string;
  integrationName: string;
}

/**
 * Authentication settings sent to / received from the HTTP runner.
 * type NONE disables enforcement; BASIC uses HTTP Basic Auth; HEADER matches a request header.
 */
export interface AuthSettings {
  type: string;
  username?: string;
  password?: string;
  headerName?: string;
  /** Matcher strategy for HEADER type: equals | contains | matches | equals_insensitive */
  matcherName?: string;
  matchingValue?: string;
}
